/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Oct 25 2023
 **/

import { useEffect, useState } from "react";

import { Configure } from "@/frameworks/libraries/utilities";
import { _T, _P } from "@rfice/language";
import {
	Button,
	Checkbox,
	Column,
	Divider,
	Row,
	TextField,
	Typography,
	useModal
} from "@/frameworks/designStuff/components";

import Layout from "../../Layout";
import * as S from "../../emotion";
import { useCommon, useUsers } from "@/frameworks/core/API";
import { ModalID } from "@/frameworks/core/commons";

interface AccountSetting {
	move: React.Dispatch<React.SetStateAction<"delete" | "setting">>;
}

const DELETE_REASON = {
	NOT_INTERESTED_IN_SERVICE: _T("더 이상 서비스에 관심이 없음"),
	NO_CONTENT: _T("이용하고 싶은 콘텐츠가 없음"),
	RE_REGISTER_N_CHANGE_ID: _T("재가입 및 아이디 변경"),
	DONT_LIKE_AVATAR_N_STYLE: _T("아바타 및 스타일이 마음에 들지 않음"),
	DIFFICULT_USE: _T("이용이 너무 어렵고 불편함"),
	SERVICE_ERROR: _T("서비스가 느리고 오류가 잦음"),
	ETC: _T("기타 (직접 입력)")
};
type Reason =
	| "NOT_INTERESTED_IN_SERVICE"
	| "NO_CONTENT"
	| "RE_REGISTER_N_CHANGE_ID"
	| "DONT_LIKE_AVATAR_N_STYLE"
	| "DIFFICULT_USE"
	| "SERVICE_ERROR"
	| "ETC";

interface ModalInterface {
	text: string | JSX.Element | JSX.Element[];
	onClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}
function ResultModal({ text, onClick }: ModalInterface) {
	return (
		<Column style={{ width: "440px", height: "175px", borderRadius: "12px" }}>
			<Row
				style={{
					width: "100%",
					height: "126px",
					padding: "28px 32px 32px 32px",
					alignItems: "center",
					textAlign: "start",
					whiteSpace: "pre-line"
				}}
			>
				<Typography typoSize="Body2" typoColor="neutral_70_100">
					{text}
				</Typography>
			</Row>
			<Divider type="line" />
			<Row style={{ width: "100%", padding: "6px 20px 10px 20px" }} horizonAlign="right">
				<Button size="large" onClick={onClick}>
					{_T("확인")}
				</Button>
			</Row>
		</Column>
	);
}

export default function DeleteAccount({ move }: AccountSetting) {
	const [reasonList, setReasonList] = useState<WebServices.Commons.GetDeleteReasonTypes.Data>([]);
	const [reason, setReason] = useState<Reason | "">("NOT_INTERESTED_IN_SERVICE");
	const [etcReason, setEtcReason] = useState("");
	const [agree, setAgree] = useState(true);
	const modal = useModal();

	const userAPI = useUsers({
		onSuccess() {
			modal.change(ModalID.SETTINGS, {
				id: ModalID.DELETE_ACCOUNT_SUCCESS,
				content: (
					<ResultModal
						text={_P(
							"탈퇴 처리가 완료되었습니다<br />그동안 Rfice 를 이용해주셔서 감사합니다<br />더욱 발전하는 Rfice가 되겠습니다"
						)}
						onClick={() => {
							modal.hideAll();
							Configure.clear_authenticated();
						}}
					/>
				),
				onHide() {
					Configure.clear_authenticated();
				}
			});
		},
		onError() {
			modal.change(ModalID.SETTINGS, {
				id: ModalID.DELETE_ACCOUNT_FAIL,
				content: (
					<ResultModal
						text={_T(
							"그룹 또는 스페이스의 유일한 관리자입니다. 다른 사람에게 관리자 지정 후 탈퇴가 가능합니다."
						)}
						onClick={() => modal.hideAll()}
					/>
				)
			});
		}
	});
	const commonAPI = useCommon({
		onSuccess() {
			const { data } = commonAPI.deleteReasonTypes.result;
			if (data) {
				setReasonList(data);
			}
		}
	});
	useEffect(() => {
		commonAPI.deleteReasonTypes.execute();
	}, []);
	const handleWithdraw = () => {
		if (reason === "ETC") {
			userAPI.withdraw.execute({
				deleteReasonType: reason,
				deleteReason: etcReason
			});
		} else {
			userAPI.withdraw.execute({
				deleteReasonType: reason
			});
		}
	};

	const handleCancel = () => {
		move("setting");
	};

	return (
		<Layout
			title="계정 설정"
			handleSave={handleWithdraw}
			hasCancel={true}
			handleCancel={handleCancel}
			disabled={!agree}
			buttonText="탈퇴하기"
		>
			<Column gap={8}>
				<Typography typoSize="Title1" typoColor="neutral_80_100" style={{}}>
					{_T("계정 탈퇴")}
				</Typography>
				<S.Info>{_T("계정 탈퇴를 진행하기 전 아래 항목을 꼭 확인해주세요.")}</S.Info>
				<S.InfoWrapper verticalAlign="distribute">
					<Typography typoSize="Body3_MD" typoColor="error_60_100">
						{_T("개인 정보 및 Rfice에서 만들어진 모든 데이터가 삭제됩니다.")}
					</Typography>
					<Column.ul gap={8}>
						<S.InfoLi>
							{_T(
								"계정탈퇴 후에는 회원님의 개인정보 복원이 불가하며, 탈퇴 진행 시 해당 계정은 90일동안 재가입이 불가합니다."
							)}
						</S.InfoLi>
						<S.InfoLi>
							{_T(
								"탈퇴한 계정은 Rfice에서 확인할 수 없으나, 해당 계정에만 국한되지 않는 콘텐츠 (예: 채팅 메시지, 파일내역, 방명록 등)는 상대방이 확인할 수 있습니다."
							)}
						</S.InfoLi>
						<S.InfoLi>{_T("계정탈퇴 시, 마케팅 정보 수신 동의(E-Mail)가 철회됩니다.")}</S.InfoLi>
					</Column.ul>
				</S.InfoWrapper>
			</Column>
			<Column gap={8} style={{ paddingBottom: "24px", width: "100%" }}>
				<Typography typoSize="Title1" typoColor="neutral_80_100">
					{_T("탈퇴 사유")}
				</Typography>
				<S.Info>{_T("탈퇴하시는 사유를 알려주세요. 서비스 개선에 중요 자료로 활용하겠습니다.")}</S.Info>
				<S.DeleteSelect
					onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setReason(ev.target.value as Reason)}
					value={DELETE_REASON[reason as Reason]}
					options={reasonList.map((reason) => ({
						label: DELETE_REASON[reason],
						value: reason
					}))}
				/>
				{reason === "ETC" ? (
					<Column style={{ width: "100%" }}>
						<Row horizonAlign="right" style={{ width: "100%" }}>
							<Typography typoSize="Body5" typoColor="neutral_60_100">
								{etcReason.length} / 100
							</Typography>
						</Row>
						<TextField
							multiple={true}
							onChange={(ev) => setEtcReason(ev.target.value)}
							kind="textarea"
							style={{ padding: "10px 12px", width: "100%", height: "80px" }}
							maxLength={100}
						/>
					</Column>
				) : null}
			</Column>
			<Checkbox onChange={() => setAgree((prev) => !prev)} checked={agree}>
				<Typography typoSize="Body3">{_T("유의사항 모두 확인했으며, 계정 탈퇴에 동의합니다.")}</Typography>
			</Checkbox>
		</Layout>
	);
}
